import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import { collection, getDocs, addDoc, query, orderBy, serverTimestamp, doc, getDoc, updateDoc, runTransaction } from "firebase/firestore";

const DEFAULT_TOTAL_FLATS = 12; // fallback only — used if Settings has no totalFlats value

// ════════════════════════════════════════════════════════════════
// HELPERS
// ════════════════════════════════════════════════════════════════
async function writeNotification({ toUid, message, type }) {
  if (!toUid) return;
  try {
    await addDoc(collection(db, "notifications"), {
      toUid, message, type,
      isRead: false,
      createdAt: serverTimestamp(),
    });
  } catch (e) {
    console.error("Notification write error:", e);
  }
}

function tsToDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (ts.seconds) return new Date(ts.seconds * 1000);
  return new Date(ts);
}

function timeLeftLabel(deadlineISO) {
  const deadline = new Date(deadlineISO);
  const now = new Date();
  const diffMs = deadline - now;
  if (diffMs <= 0) return "Closed";
  const hrs = Math.floor(diffMs / (1000 * 60 * 60));
  if (hrs < 1) {
    const mins = Math.floor(diffMs / (1000 * 60));
    return `${mins} min left`;
  }
  if (hrs < 24) return `${hrs} hr${hrs !== 1 ? "s" : ""} left`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days !== 1 ? "s" : ""} left`;
}

function closeDateLabel(dateVal) {
  const d = tsToDate(dateVal) || new Date(dateVal);
  if (!d || isNaN(d)) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) +
    " " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

function nextElectionDateLabel() {
  const now = new Date();
  const year = now.getMonth() > 2 || (now.getMonth() === 2 && now.getDate() > 15)
    ? now.getFullYear() + 1
    : now.getFullYear();
  return `March 15, ${year}`;
}

// ════════════════════════════════════════════════════════════════
// VOTING — frozen design, 13 Jul 2026
//   - One vote per FLAT, stored in a separate subcollection
//     (polls/{pollId}/votes/{flatId}) so the app never has to pull
//     up or display which flat voted which way — it only ever
//     reads/writes the yesCount/noCount totals on the poll itself.
//   - General polls: anyone logged in from a flat can cast that
//     flat's vote (owner, tenant, or family member).
//   - Task-based (cost) polls: only a login with memberType "owner"
//     can vote — tenants/family don't see these polls at all
//     (filtered out further down).
// ════════════════════════════════════════════════════════════════
async function castVote(pollId, flatId, choice, voter) {
  const pollRef = doc(db, "polls", pollId);
  const voteRef = doc(db, "polls", pollId, "votes", flatId);

  await runTransaction(db, async (transaction) => {
    const voteSnap = await transaction.get(voteRef);
    if (voteSnap.exists()) {
      throw new Error("ALREADY_VOTED");
    }

    const pollSnap = await transaction.get(pollRef);
    if (!pollSnap.exists()) throw new Error("POLL_NOT_FOUND");
    const pollData = pollSnap.data();

    if (pollData.status !== "active" || new Date(pollData.deadline) <= new Date()) {
      throw new Error("POLL_CLOSED");
    }

    transaction.set(voteRef, {
      choice,                                   // "yes" | "no" — never read back by the UI
      votedByName: voter.name || "Unknown",
      votedByUid:  voter.uid || voter.id || "",
      memberType:  voter.memberType || "owner",
      votedAt:     serverTimestamp(),
    });

    const currentVoteCount = pollData.voteCount || 0;
    const currentYes       = pollData.yesCount  || 0;
    const currentNo        = pollData.noCount   || 0;

    transaction.update(pollRef, {
      voteCount: currentVoteCount + 1,
      yesCount:  choice === "yes" ? currentYes + 1 : currentYes,
      noCount:   choice === "no"  ? currentNo  + 1 : currentNo,
    });
  });
}

// Runs the moment anyone opens the Polls screen and finds a poll whose
// deadline has passed while it was still "active". Decides Passed/Failed
// and writes it once — first person to open the screen after the
// deadline effectively closes it for everyone.
async function closeExpiredPollIfNeeded(poll) {
  if (poll.status !== "active") return null;
  if (new Date(poll.deadline) > new Date()) return null;

  const yesCount = poll.yesCount || 0;
  const noCount  = poll.noCount  || 0;

  let result;
  if (poll.mandatory) {
    // Cost poll ₹10,000+: passes once Yes reaches the 2/3-of-total-flats
    // threshold locked in at creation time. Full 12/12 participation
    // is NOT required.
    result = yesCount >= (poll.yesNeeded || 0) ? "passed" : "failed";
  } else {
    // General / smaller polls: simple majority of votes actually cast.
    // A tie counts as Failed.
    result = yesCount > noCount ? "passed" : "failed";
  }

  const pollRef = doc(db, "polls", poll.id);
  try {
    await updateDoc(pollRef, {
      status:     "closed",
      result,
      autoClosed: true,
      closedAt:   serverTimestamp(),
    });
  } catch (e) {
    console.error("Auto-close failed for poll", poll.id, e);
    return null;
  }

  return { ...poll, status: "closed", result, autoClosed: true };
}

// Badge styling per poll type — colors match the frozen pill design
const BADGES = {
  task:            { label: "Task",            bg: "#F5C4B320", text: "#D85A30", border: "#D85A3040" },
  general:         { label: "General",         bg: "#CECBF620", text: "#7F77DD", border: "#7F77DD40" },
  election_trigger:{ label: "Election trigger", bg: "#FAC77520", text: "#BA7517", border: "#BA751740" },
};

// ── Shared styles ─────────────────────────────────────────────
const labelStyle = {
  fontSize: "11px", color: "#8888a8", textTransform: "uppercase",
  letterSpacing: "0.5px", fontWeight: "600", marginBottom: "8px", display: "block",
};

const inputStyle = {
  width: "100%", background: "#0f0f13", border: "1px solid #2e2e40",
  borderRadius: "8px", padding: "10px 12px", color: "#e8e8f0",
  fontSize: "14px", outline: "none", boxSizing: "border-box", fontFamily: "inherit",
};

const MANDATORY_THRESHOLD = 10000; // ₹ — matches Section 2.3 of the frozen Poll design

// ════════════════════════════════════════════════════════════════
// Modal shell — matches the same look used across the app
// ════════════════════════════════════════════════════════════════
function ModalShell({ title, onClose, children }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "#000000cc", zIndex: 1000,
      display: "flex", alignItems: "flex-end", justifyContent: "center",
    }}>
      <div style={{
        background: "#1a1a24", borderRadius: "20px 20px 0 0",
        width: "100%", maxWidth: "640px",
        maxHeight: "90vh",
        display: "flex", flexDirection: "column",
        overflow: "hidden",
      }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "16px 20px", borderBottom: "1px solid #2e2e40",
          flexShrink: 0,
        }}>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "#e8e8f0" }}>{title}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#8888a8", fontSize: "22px", cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: "16px 20px", overflowY: "auto", flex: 1 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// CreatePollModal — the "+ Create Poll" popup
// Fields (frozen design): Title → Type (Task/General) →
//   Estimated cost (if Task) → Voting Window (Normal/Urgent)
// Creator is always whoever is logged in when they submit — never
// hardcoded to any one person.
// ════════════════════════════════════════════════════════════════
function CreatePollModal({ onClose, onDone, user, totalFlats = DEFAULT_TOTAL_FLATS }) {
  const [title, setTitle]           = useState("");
  const [pollType, setPollType]     = useState("general"); // "general" | "task"
  const [estCost, setEstCost]       = useState("");
  const [votingWindow, setVotingWindow] = useState("normal"); // "normal" (72h) | "urgent" (24h)
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState("");

  const isTask      = pollType === "task";
  const costNum      = Number(estCost) || 0;
  const isMandatory  = isTask && costNum >= MANDATORY_THRESHOLD;
  // Cost decisions ₹10,000+ always get a fixed 7-day window — the
  // Normal/Urgent picker only applies to everything else.
  const windowHours  = isMandatory ? 168 : (votingWindow === "urgent" ? 24 : 72);
  // Roughly two-thirds of this society's flats must say Yes for a
  // mandatory (big-expense) poll to pass. For BSKR's 12 flats this
  // works out to 8, same as the original fixed rule.
  const yesNeeded    = Math.ceil(totalFlats * 2 / 3);

  function validate() {
    if (!title.trim()) return "Please enter a poll question or title.";
    if (isTask && (!estCost || costNum <= 0)) return "Please enter the estimated cost for this task-based poll.";
    return null;
  }

  async function handleSubmit() {
    const err = validate();
    if (err) { setError(err); return; }

    setSaving(true);
    setError("");
    try {
      const now = new Date();
      const deadline = new Date(now.getTime() + windowHours * 60 * 60 * 1000);

      const creatorName = user?.name || "Unknown";
      const creatorFlat = user?.flatId || "";
      const creatorUid  = user?.id || user?.uid || "";

      const pollRef = await addDoc(collection(db, "polls"), {
        title:          title.trim(),
        type:           pollType,               // "task" | "general"
        estimatedCost:  isTask ? costNum : null,
        mandatory:      isMandatory,             // true only if Task + ₹10,000 or above
        yesNeeded:      isMandatory ? yesNeeded : null, // locked in now, so it can't drift if totalFlats changes later
        votingWindowHours: windowHours,
        deadline:       deadline.toISOString(),
        status:         "active",                // "active" | "closed"
        voteCount:      0,
        yesCount:       0,
        noCount:        0,
        closedByUid:    null,
        closedByName:   null,
        closedAt:       null,
        autoClosed:     false,
        createdByUid:   creatorUid,
        createdByName:  creatorName,
        createdByFlat:  creatorFlat,
        createdAt:      serverTimestamp(),
      });

      // Broadcast to all flats — every poll goes to all 12
      await writeNotification({
        toUid:   "all",
        message: `🗳️ New poll from ${creatorName} (${creatorFlat}): "${title.trim()}"${isMandatory ? " — response required from all flats." : ""}`,
        type:    "poll_created",
      });

      if (onDone) onDone(pollRef.id);
      onClose();
    } catch (e) {
      console.error(e);
      setError("Failed to create poll. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell title="🗳️ Create Poll" onClose={onClose}>
      {/* Question / Title */}
      <div style={{ marginBottom: "14px" }}>
        <div style={labelStyle}>Question / title</div>
        <textarea
          value={title}
          onChange={e => setTitle(e.target.value)}
          rows={2}
          style={{ ...inputStyle, resize: "vertical" }}
          placeholder="e.g. Should we hire the new security guard?"
        />
      </div>

      {/* Poll Type */}
      <div style={{ marginBottom: "14px" }}>
        <div style={labelStyle}>Poll type</div>
        <div style={{ display: "flex", gap: "8px" }}>
          {[
            { key: "general", label: "General", desc: "Opinion / community question" },
            { key: "task",    label: "Task-based", desc: "Linked to a cost or event" },
          ].map(opt => (
            <button
              key={opt.key}
              onClick={() => setPollType(opt.key)}
              style={{
                flex: 1, textAlign: "left",
                padding: "10px 12px", borderRadius: "10px",
                border: pollType === opt.key ? "1px solid #6c63ff" : "1px solid #2e2e40",
                background: pollType === opt.key ? "#6c63ff20" : "#0f0f13",
                cursor: "pointer",
              }}
            >
              <div style={{ fontSize: "13px", fontWeight: "600", color: pollType === opt.key ? "#6c63ff" : "#e8e8f0" }}>
                {opt.label}
              </div>
              <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>
                {opt.desc}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Estimated cost — only for Task-based */}
      {isTask && (
        <div style={{ marginBottom: "14px" }}>
          <div style={labelStyle}>Estimated cost (₹)</div>
          <input
            type="number"
            value={estCost}
            onChange={e => setEstCost(e.target.value)}
            style={inputStyle}
            placeholder="e.g. 15000"
          />
          {costNum > 0 && (
            <div style={{
              marginTop: "8px", fontSize: "12px",
              color: isMandatory ? "#ffa726" : "#8888a8",
            }}>
              {isMandatory
                ? `⚠️ This is a big amount (₹${costNum.toLocaleString("en-IN")}). All ${totalFlats} flats must vote on this, and at least ${yesNeeded} must say Yes for it to pass.`
                : `This is a smaller amount — flat owners can choose whether to respond.`}
            </div>
          )}
        </div>
      )}

      {/* Voting Window */}
      <div style={{ marginBottom: "16px" }}>
        <div style={labelStyle}>Voting window</div>
        {isMandatory ? (
          <div style={{
            padding: "10px 12px", borderRadius: "10px",
            border: "1px solid #6c63ff", background: "#6c63ff20",
            fontSize: "12px", color: "#e8e8f0",
          }}>
            🗓️ Fixed at 7 days — cost decisions of ₹10,000 or more always get a full week for flats to respond.
          </div>
        ) : (
          <div style={{ display: "flex", gap: "8px" }}>
            {[
              { key: "normal", label: "Normal", desc: "Closes in 72 hrs" },
              { key: "urgent", label: "Urgent", desc: "Closes in 24 hrs" },
            ].map(opt => (
              <button
                key={opt.key}
                onClick={() => setVotingWindow(opt.key)}
                style={{
                  flex: 1, textAlign: "left",
                  padding: "10px 12px", borderRadius: "10px",
                  border: votingWindow === opt.key ? "1px solid #6c63ff" : "1px solid #2e2e40",
                  background: votingWindow === opt.key ? "#6c63ff20" : "#0f0f13",
                  cursor: "pointer",
                }}
              >
                <div style={{ fontSize: "13px", fontWeight: "600", color: votingWindow === opt.key ? "#6c63ff" : "#e8e8f0" }}>
                  {opt.label}
                </div>
                <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>
                  {opt.desc}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{
          background: "#ff525215", border: "1px solid #ff525240",
          borderRadius: "8px", padding: "9px 12px",
          fontSize: "12px", color: "#ff5252", marginBottom: "12px",
        }}>
          ⚠ {error}
        </div>
      )}

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={saving}
        style={{
          width: "100%",
          background: saving ? "#3a3a50" : "#6c63ff",
          color: saving ? "#8888a8" : "#fff",
          border: "none", borderRadius: "10px", padding: "13px",
          fontSize: "15px", fontWeight: "600",
          cursor: saving ? "not-allowed" : "pointer",
        }}
      >
        {saving ? "Creating…" : "Create Poll"}
      </button>
    </ModalShell>
  );
}

// ════════════════════════════════════════════════════════════════
// Poll Row — used in both Active and Archive tabs
// ════════════════════════════════════════════════════════════════
function PollRow({ poll, isArchive, totalFlats = DEFAULT_TOTAL_FLATS, user, onVoted }) {
  const badge = BADGES[poll.type] || BADGES.general;

  const [checkingVote, setCheckingVote] = useState(!isArchive);
  const [hasVoted, setHasVoted]         = useState(false);
  const [voting, setVoting]             = useState(false);
  const [voteError, setVoteError]       = useState("");

  const flatId       = user?.flatId || null;
  const isCostPoll   = poll.type === "task";
  // Cost-based polls: only a memberType "owner" login may vote.
  // General polls: anyone logged in from a flat may vote.
  const isEligible   = !!flatId && (!isCostPoll || user?.memberType === "owner");

  // Check once (on mount) whether this flat has already voted on this poll.
  useEffect(() => {
    if (isArchive || !flatId) { setCheckingVote(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const voteSnap = await getDoc(doc(db, "polls", poll.id, "votes", flatId));
        if (!cancelled) setHasVoted(voteSnap.exists());
      } catch (e) {
        console.error("Could not check vote status:", e);
      } finally {
        if (!cancelled) setCheckingVote(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [poll.id, flatId, isArchive]);

  async function handleVote(choice) {
    if (!flatId || voting) return;
    setVoting(true);
    setVoteError("");
    try {
      await castVote(poll.id, flatId, choice, user);
      setHasVoted(true);
      if (onVoted) onVoted();
    } catch (e) {
      if (e.message === "ALREADY_VOTED") {
        setHasVoted(true); // someone else from this flat beat them to it
      } else if (e.message === "POLL_CLOSED") {
        setVoteError("This poll just closed — voting has ended.");
      } else {
        console.error(e);
        setVoteError("Couldn't record your vote. Please try again.");
      }
    } finally {
      setVoting(false);
    }
  }

  return (
    <div style={{
      background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px",
      padding: "12px 14px", marginBottom: "8px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px" }}>
        <div style={{ fontSize: "14px", fontWeight: "600", color: "#e8e8f0", flex: 1 }}>
          {poll.title}
        </div>
        <span style={{
          background: badge.bg, color: badge.text, border: `1px solid ${badge.border}`,
          fontSize: "11px", fontWeight: "600", padding: "3px 10px",
          borderRadius: "999px", whiteSpace: "nowrap",
        }}>
          {badge.label}
        </span>
      </div>

      <div style={{ fontSize: "12px", color: "#8888a8", marginTop: "6px" }}>
        {isArchive ? (
          <>
            {poll.result === "passed" ? "✅ Passed" : poll.result === "failed" ? "❌ Failed" : "Closed"}
            {" · "}{poll.yesCount || 0} Yes, {poll.noCount || 0} No
            {" · "}{closeDateLabel(poll.closedAt || poll.deadline)}
          </>
        ) : (
          <>{timeLeftLabel(poll.deadline)} · {poll.voteCount || 0} of {totalFlats} flats responded</>
        )}
      </div>

      <div style={{ fontSize: "11px", color: "#555570", marginTop: "4px" }}>
        Created by {poll.createdByName || "Unknown"}{poll.createdByFlat ? ` (${poll.createdByFlat})` : ""}
        {isArchive && (
          <>
            {" · "}
            {poll.autoClosed
              ? "Auto-closed"
              : poll.closedByName
                ? `Closed by ${poll.closedByName}`
                : "Auto-closed"}
          </>
        )}
      </div>

      {/* Voting area — Active tab only */}
      {!isArchive && (
        <div style={{ marginTop: "12px" }}>
          {checkingVote ? null : hasVoted ? (
            <div style={{
              background: "#4caf5015", border: "1px solid #4caf5040", borderRadius: "8px",
              padding: "9px 12px", fontSize: "12px", color: "#4caf50", fontWeight: "600",
            }}>
              ✅ Your flat's vote has been recorded
            </div>
          ) : !isEligible ? (
            <div style={{
              background: "#0f0f13", border: "1px dashed #2e2e40", borderRadius: "8px",
              padding: "9px 12px", fontSize: "12px", color: "#8888a8",
            }}>
              {isCostPoll
                ? "This poll involves a cost — only your flat owner's login can vote. Please ask them to respond."
                : "You're not linked to a flat, so voting isn't available on this login."}
            </div>
          ) : (
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => handleVote("yes")}
                disabled={voting}
                style={{
                  flex: 1, padding: "10px", borderRadius: "8px", border: "1px solid #4caf5060",
                  background: "#4caf5020", color: "#4caf50", fontSize: "13px", fontWeight: "700",
                  cursor: voting ? "not-allowed" : "pointer",
                }}
              >
                👍 Yes
              </button>
              <button
                onClick={() => handleVote("no")}
                disabled={voting}
                style={{
                  flex: 1, padding: "10px", borderRadius: "8px", border: "1px solid #ff525260",
                  background: "#ff525220", color: "#ff5252", fontSize: "13px", fontWeight: "700",
                  cursor: voting ? "not-allowed" : "pointer",
                }}
              >
                👎 No
              </button>
            </div>
          )}
          {voteError && (
            <div style={{ marginTop: "8px", fontSize: "12px", color: "#ff5252" }}>⚠ {voteError}</div>
          )}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// Elections tab — disabled until the actual voting window
// ════════════════════════════════════════════════════════════════
function ElectionsTab() {
  const [showInfo, setShowInfo] = useState(false);

  return (
    <div>
      <button
        onClick={() => setShowInfo(true)}
        style={{
          width: "100%", textAlign: "center", padding: "40px 20px",
          background: "#1a1a24", border: "1px dashed #2e2e40", borderRadius: "12px",
          color: "#555570", cursor: "pointer",
        }}
      >
        <div style={{ fontSize: "32px", marginBottom: "10px" }}>🗳️</div>
        <div style={{ fontSize: "14px", fontWeight: "600" }}>Elections are currently closed</div>
        <div style={{ fontSize: "12px", marginTop: "4px" }}>Tap to see the next election date</div>
      </button>

      {showInfo && (
        <div style={{
          marginTop: "10px", background: "#6c63ff12", border: "1px solid #6c63ff30",
          borderRadius: "10px", padding: "12px 14px", fontSize: "13px", color: "#e8e8f0",
        }}>
          📅 Next election: {nextElectionDateLabel()}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// PollScreen — main export
// ════════════════════════════════════════════════════════════════
export default function PollScreen() {
  const { user } = useAuth();
  const [tab, setTab] = useState("active"); // "active" | "archive" | "elections"
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [totalFlats, setTotalFlats] = useState(DEFAULT_TOTAL_FLATS);

  useEffect(() => {
    loadPolls();
    (async () => {
      try {
        const settSnap = await getDoc(doc(db, "settings", "init"));
        if (settSnap.exists() && Number(settSnap.data().totalFlats) > 0) {
          setTotalFlats(Number(settSnap.data().totalFlats));
        }
      } catch (e) {
        console.error("Could not load totalFlats, using fallback:", e);
      }
    })();
    // eslint-disable-next-line
  }, []);

  async function loadPolls() {
    setLoading(true);
    try {
      const q = query(collection(db, "polls"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      let loaded = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Any poll still marked "active" whose deadline has passed gets
      // closed right now, by whoever happened to open this screen first.
      const now = new Date();
      const expired = loaded.filter(p => p.status === "active" && new Date(p.deadline) <= now);
      if (expired.length > 0) {
        const closed = await Promise.all(expired.map(closeExpiredPollIfNeeded));
        const closedById = new Map(closed.filter(Boolean).map(p => [p.id, p]));
        loaded = loaded.map(p => closedById.get(p.id) || p);
      }

      setPolls(loaded);
    } catch (e) {
      console.error("Failed to load polls:", e);
    } finally {
      setLoading(false);
    }
  }

  const now = new Date();
  // Cost-based (task) polls are hidden entirely from anyone who isn't
  // a flat-owner login — tenants/family won't see them in Active Polls at all.
  const activePolls  = polls.filter(p => {
    if (p.status !== "active" || new Date(p.deadline) <= now) return false;
    if (p.type === "task" && (!user?.flatId || user?.memberType !== "owner")) return false;
    return true;
  });
  const archivePolls = polls.filter(p => p.status === "closed" || new Date(p.deadline) <= now);

  const tabs = [
    { id: "active",    label: "Active Polls" },
    { id: "archive",   label: "Archive" },
    { id: "elections", label: "Elections" },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: "16px" }}>
        <div style={{ fontSize: "15px", fontWeight: "700", color: "#e8e8f0" }}>
          🗳️ Polls &amp; Elections
        </div>
        <div style={{ fontSize: "12px", color: "#555570", marginTop: "3px" }}>
          Your vote stays private — only the final result is shared with all flat owners.
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: "flex", gap: "6px", marginBottom: "16px" }}>
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              flex: 1, padding: "9px 8px", borderRadius: "10px",
              border: tab === t.id ? "1px solid #6c63ff" : "1px solid #2e2e40",
              background: tab === t.id ? "#6c63ff20" : "#1a1a24",
              color: tab === t.id ? "#6c63ff" : "#8888a8",
              fontSize: "12px", fontWeight: "600", cursor: "pointer",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Active Polls tab */}
      {tab === "active" && (
        <div>
          <button
            onClick={() => setShowCreate(true)}
            style={{
              width: "100%", background: "#6c63ff", color: "#fff", border: "none",
              borderRadius: "10px", padding: "12px", fontSize: "14px", fontWeight: "600",
              cursor: "pointer", marginBottom: "14px",
            }}
          >
            + Create Poll
          </button>

          {loading ? (
            <div style={{ textAlign: "center", padding: "32px", color: "#555570", fontSize: "13px" }}>
              Loading…
            </div>
          ) : activePolls.length === 0 ? (
            <div style={{
              background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px",
              padding: "24px", textAlign: "center", color: "#555570", fontSize: "13px",
            }}>
              No active polls right now.
            </div>
          ) : (
            activePolls.map(p => (
              <PollRow key={p.id} poll={p} isArchive={false} totalFlats={totalFlats} user={user} onVoted={loadPolls} />
            ))
          )}
        </div>
      )}

      {/* Archive tab */}
      {tab === "archive" && (
        <div>
          {loading ? (
            <div style={{ textAlign: "center", padding: "32px", color: "#555570", fontSize: "13px" }}>
              Loading…
            </div>
          ) : archivePolls.length === 0 ? (
            <div style={{
              background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px",
              padding: "24px", textAlign: "center", color: "#555570", fontSize: "13px",
            }}>
              No closed polls yet.
            </div>
          ) : (
            archivePolls.map(p => <PollRow key={p.id} poll={p} isArchive={true} totalFlats={totalFlats} user={user} />)
          )}
        </div>
      )}

      {/* Elections tab */}
      {tab === "elections" && <ElectionsTab />}

      {/* Create Poll modal */}
      {showCreate && (
        <CreatePollModal
          user={user}
          totalFlats={totalFlats}
          onClose={() => setShowCreate(false)}
          onDone={() => { setShowCreate(false); loadPolls(); }}
        />
      )}
    </div>
  );
}
